import { useEffect, useRef, useState } from "react";
import { ImageFallback } from "./ImageFallback";

type ProductImageProps = {
  // undefined = still resolving the URL, null = resolved, no image.
  src?: string | null;
  alt: string;
  className?: string;
};

// Keeps the skeleton up until the browser has actually finished loading
// the image (onLoad), not just until we know its URL - otherwise there's
// a gap where the <img> is mounted but still blank/decoding, which some
// browsers fill with the alt text.
export function ProductImage({ src, alt, className = "" }: ProductImageProps) {
  const [loaded, setLoaded] = useState(false);
  const [broken, setBroken] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    setLoaded(false);
    setBroken(false);
  }, [src]);

  // WebKit (Safari, and Brave on iOS) can resolve an <img> synchronously
  // from its HTTP cache when the same URL was already loaded elsewhere in
  // the app (e.g. this variant's picture already shown on the home page) -
  // faster than React's onLoad listener attaches, so the event is missed
  // and the image stays stuck at opacity-0. Checking `complete` here covers
  // that case as a fallback to the onLoad handler below.
  useEffect(() => {
    if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [src]);

  const hasImage = typeof src === "string";
  const showSkeleton = src === undefined || (hasImage && !loaded && !broken);
  const showFallback = src === null || (hasImage && broken);

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {showSkeleton && <div className="absolute inset-0 bg-white/60 animate-pulse" />}
      {showFallback && <ImageFallback className="absolute inset-0" />}
      {hasImage && !broken && (
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          draggable={false}
          onLoad={() => setLoaded(true)}
          onError={() => setBroken(true)}
          className={`absolute inset-0 w-full h-full object-contain bg-white transition-opacity duration-150 ${
            loaded ? "opacity-100" : "opacity-0"
          }`}
        />
      )}
    </div>
  );
}
